import { apiFetch } from "./api.js";
import { t, setLocale } from "./i18n.js";
import { escapeHtml, toast } from "./util.js";
import { logout } from "./auth.js";

/** Pure: why a delivery toggle is disabled, or null if it's live. Spec:
 *  with no address on file every toggle is inert (send_letter_email requires
 *  both the flag and a non-empty address) — render disabled with a hint
 *  rather than a switch that silently changes nothing. */
export function deliveryToggleDisabledReason(hasEmail: boolean): string | null {
  return hasEmail ? null : t("settings.delivery_needs_email");
}

interface DeliveryEntry { group_id: string; group_name: string; enabled: boolean }

export async function loadPersonalSettingsView(root: HTMLElement): Promise<void> {
  root.innerHTML = `<p>${t("settings.loading")}</p>`;
  let emailState: { has_any_email: boolean };
  let delivery: { has_email: boolean; memberships: DeliveryEntry[] };
  let me: { display_name: string; locale: string };
  try {
    [emailState, delivery, me] = await Promise.all([
      apiFetch("/me/email-state"),
      apiFetch("/me/delivery"),
      apiFetch("/me"),
    ]);
  } catch {
    root.innerHTML = `<p class="feed-empty">${t("settings.load_failed")}</p>`;
    return;
  }
  render(root, emailState.has_any_email, delivery, me);
}

function render(
  root: HTMLElement,
  hasAnyEmail: boolean,
  delivery: { has_email: boolean; memberships: DeliveryEntry[] },
  me: { display_name: string; locale: string }
): void {
  const disabledReason = deliveryToggleDisabledReason(delivery.has_email);
  root.innerHTML = `
    <h2>${t("settings.title")}</h2>
    <div class="form-group">
      <label>${t("settings.email_label")}</label>
      <p id="settings-email-display"></p>
      <button class="btn-secondary" id="settings-email-edit">${t("settings.edit")}</button>
      <div id="settings-email-form" style="display:none;margin-top:.5rem">
        <input id="settings-email-input" type="email" placeholder="${t("email_ask.placeholder")}">
        <button class="btn" id="settings-email-save">${t("email_ask.save")}</button>
      </div>
    </div>
    <div class="form-group">
      <label>${t("settings.delivery_label")}</label>
      ${disabledReason ? `<p class="notice-banner" style="display:block">${disabledReason}</p>` : ""}
      <ul style="list-style:none">
        ${delivery.memberships.map((m) => `
          <li style="padding:.35rem 0;display:flex;justify-content:space-between;align-items:center">
            <span>${escapeHtml(m.group_name)}</span>
            <input type="checkbox" data-gid="${m.group_id}" class="settings-delivery-toggle"
              ${m.enabled ? "checked" : ""} ${disabledReason ? "disabled" : ""}>
          </li>`).join("")}
      </ul>
    </div>
    <div class="form-group">
      <label>${t("settings.display_name_label")}</label>
      <input type="text" id="settings-display-name" value="${escapeHtml(me.display_name)}">
      <button class="btn" id="settings-save-name">${t("compose.save")}</button>
    </div>
    <div class="form-group">
      <label>${t("settings.language_label")}</label>
      <select id="settings-locale">
        <option value="" ${me.locale === "" ? "selected" : ""}>${t("settings.language_auto")}</option>
        <option value="en" ${me.locale === "en" ? "selected" : ""}>English</option>
        <option value="pt-BR" ${me.locale === "pt-BR" ? "selected" : ""}>Português</option>
      </select>
    </div>
    <div class="form-group">
      <button class="btn-secondary" id="settings-logout">${t("app.logout")}</button>
      <button class="btn-secondary" id="settings-logout-all">${t("settings.logout_all")}</button>
    </div>
  `;

  const emailDisplay = root.querySelector<HTMLElement>("#settings-email-display")!;
  emailDisplay.textContent = hasAnyEmail ? "•••• (on file)" : t("settings.no_email");

  root.querySelector("#settings-email-edit")!.addEventListener("click", () => {
    const form = root.querySelector<HTMLElement>("#settings-email-form")!;
    form.style.display = form.style.display === "none" ? "block" : "none";
  });
  root.querySelector("#settings-email-save")!.addEventListener("click", async () => {
    const input = root.querySelector<HTMLInputElement>("#settings-email-input")!;
    const btn = root.querySelector<HTMLButtonElement>("#settings-email-save")!;
    btn.disabled = true;
    try {
      await apiFetch("/me/email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email_address: input.value.trim() }),
      });
      toast(t("compose.saved"));
      await loadPersonalSettingsView(root);
    } catch (e: any) {
      btn.disabled = false;
      toast(e.message || t("email_ask.err_save"));
    }
  });

  root.querySelectorAll<HTMLInputElement>(".settings-delivery-toggle").forEach((cb) => {
    cb.addEventListener("change", async () => {
      const gid = cb.dataset.gid!;
      cb.disabled = true;
      try {
        await apiFetch("/me/delivery", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ group_id: gid, enabled: cb.checked }),
        });
      } catch (e: any) {
        cb.checked = !cb.checked;
        toast(e.message || t("settings.delivery_save_failed"));
      } finally {
        cb.disabled = false;
      }
    });
  });

  root.querySelector("#settings-logout")!.addEventListener("click", logout);
  root.querySelector("#settings-logout-all")!.addEventListener("click", async () => {
    if (!confirm(t("settings.logout_all_confirm"))) return;
    try {
      await apiFetch("/me/logout-all", { method: "POST" });
    } catch { /* token may already be dead; fall through to local logout */ }
    await logout();
  });

  root.querySelector("#settings-save-name")!.addEventListener("click", async () => {
    const val = root.querySelector<HTMLInputElement>("#settings-display-name")!.value.trim();
    const btn = root.querySelector<HTMLButtonElement>("#settings-save-name")!;
    btn.disabled = true;
    try {
      await apiFetch("/me/display-name", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ display_name: val }),
      });
      toast(t("compose.saved"));
    } catch (e: any) {
      toast(e.message || t("settings.delivery_save_failed"));
    } finally {
      btn.disabled = false;
    }
  });

  root.querySelector("#settings-locale")!.addEventListener("change", async (e) => {
    const val = (e.target as HTMLSelectElement).value;
    const select = e.target as HTMLSelectElement;
    setLocale(val as any);
    select.disabled = true;
    try {
      await apiFetch("/me/locale", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locale: val }),
      });
      window.location.reload();
    } catch (err: any) {
      select.disabled = false;
      toast(err.message || t("settings.delivery_save_failed"));
    }
  });
}
